/**
 * Pure color math utilities for deriving muted/border/foreground
 * CSS variable overrides from tenant app background & text colors.
 *
 * No 'server-only' or 'use client' — importable from both contexts.
 */

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})$/.exec(
    hex,
  );

  if (!result) return { r: 0, g: 0, b: 0 };

  return {
    r: parseInt(result[1]!, 16),
    g: parseInt(result[2]!, 16),
    b: parseInt(result[3]!, 16),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((v) =>
        Math.round(Math.max(0, Math.min(255, v)))
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  );
}

function luminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const s = c / 255;

    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * rs! + 0.7152 * gs! + 0.0722 * bs!;
}

export function isDarkColor(hex: string): boolean {
  return luminance(hex) < 0.5;
}

/**
 * WCAG 2.x relative-contrast ratio between two hex colors (1 = identical,
 * 21 = black on white). Used by the website builder to warn a tenant before
 * they publish an unreadable canvas/ink or accent color — the pickers are
 * free-form, so nothing else stops `#ffffff` text on a `#ffffff` canvas.
 */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = luminance(hexA);
  const b = luminance(hexB);
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Derive --muted from background: shift slightly toward mid-gray.
 * Dark bg → slightly lighter; light bg → slightly darker.
 */
export function deriveMutedColor(bgHex: string): string {
  const { r, g, b } = hexToRgb(bgHex);
  const shift = isDarkColor(bgHex) ? 20 : -15;

  return rgbToHex(r + shift, g + shift, b + shift);
}

/**
 * Derive --muted-foreground: blend text color 60% toward background
 * to produce a "dimmed" text color.
 */
export function deriveMutedForeground(textHex: string, bgHex?: string): string {
  const text = hexToRgb(textHex);
  const bg = bgHex ? hexToRgb(bgHex) : { r: 255, g: 255, b: 255 };
  const alpha = 0.6;

  return rgbToHex(
    text.r * alpha + bg.r * (1 - alpha),
    text.g * alpha + bg.g * (1 - alpha),
    text.b * alpha + bg.b * (1 - alpha),
  );
}

/**
 * Derive --border from background: subtle contrast line.
 */
export function deriveBorderColor(bgHex: string): string {
  const { r, g, b } = hexToRgb(bgHex);
  const shift = isDarkColor(bgHex) ? 30 : -20;

  return rgbToHex(r + shift, g + shift, b + shift);
}

/**
 * Pick a readable foreground (white or black) for a given background.
 * Used to derive --primary-foreground / --secondary-foreground when a
 * tenant overrides --primary / --secondary, so badge/button labels stay
 * legible regardless of the chosen brand color.
 */
export function deriveContrastingForeground(bgHex: string): string {
  // Pick whichever extreme actually contrasts more, rather than thresholding on
  // luminance < 0.5. The WCAG-optimal crossover is ~0.179, not 0.5, so the old
  // test handed WHITE to every mid-tone background: 69 of the 256 greys came
  // back below the 4.5:1 body-text floor (#aaaaaa → 2.32:1). Choosing the
  // higher-contrast option is never worse and brings that count to 0.
  //
  // Load-bearing in three places: the landing canvas ink (landing-renderer),
  // --primary-foreground for the tenant brand (get-branding-style) and the
  // native accent (use-native-presentation).
  return contrastRatio(bgHex, '#000000') >= contrastRatio(bgHex, '#ffffff')
    ? '#000000'
    : '#ffffff';
}

/** Subtle brand tint with readable selected-state ink on light or dark surfaces. */
export function deriveSelectionColors(
  background: string,
  accent: string,
  ink: string,
) {
  const blend = (base: string, tint: string, amount: number) => {
    const a = hexToRgb(base);
    const b = hexToRgb(tint);
    return rgbToHex(
      a.r + (b.r - a.r) * amount,
      a.g + (b.g - a.g) * amount,
      a.b + (b.b - a.b) * amount,
    );
  };
  const fill = blend(background, accent, 0.14);
  const fillLuminance = luminance(fill);
  const contrast = (color: string) => {
    const value = luminance(color);
    return (
      (Math.max(value, fillLuminance) + 0.05) /
      (Math.min(value, fillLuminance) + 0.05)
    );
  };
  const target =
    contrast('#000000') > contrast('#ffffff') ? '#000000' : '#ffffff';
  let foreground = ink;
  for (let step = 1; contrast(foreground) < 4.5 && step <= 20; step++) {
    foreground = blend(ink, target, step / 20);
  }
  return { background: fill, foreground, border: foreground };
}
